import React, {useEffect, useContext, useMemo} from 'react';
import { Link } from "@react-navigation/web";
import {FormattedMessage, useIntl, IntlContext} from 'react-intl';
import { Formik, Form } from "formik";
import * as Yup from 'yup';
import {config} from '@react-spring/web'
import useScrollTo from 'react-spring-scroll-to-hook'
import WETTabs from '../../../components/WETTabs';
import SubmitTabBody from '../../../components/SubmitTabBody';
import {constants} from '../../../constants'
import AppContext from '../../../components/AppContext';
import { API, Auth } from 'aws-amplify';
import { currentLang, currentLangParam, currentLongLang } from '../../../components/components/utils'
import AccountInfoTabBody from './components/AccountInfoTabBody';
import ContactInfoTabBody from './components/ContactInfoTabBody';
import PostalCodes from 'postal-codes-js';
import ErrorValidationSpan from './components/ErrorValidationSpan';
import PopupLink from '../../../templates/PopupLink';

function Account_Edit({navigation}) {
	const appContext = useContext(AppContext);

	const { messages } = useContext(IntlContext);

	const labels = useMemo(() => {
		const formatValues = {
			p: (chunks) => <p>{chunks}</p>,
			a: (chunks) => (
				<a
					href={`https://www.cbsa-asfc.gc.ca/contact/bis-sif-${document.documentElement.lang === "fr" ? "fra" : "eng"}.html`}
					className="App-link"
					target="_blank"
					rel="noreferrer"
				>
					{chunks}
				</a>
			),
		};

		return {
			"common.maxLength.5": (<FormattedMessage id="common.maxLength.args" values={{ num: "5" }} />),
			"common.maxLength.15": (<FormattedMessage id="common.maxLength.args" values={{ num: "5" }} />),
			"common.maxLength.25": (<FormattedMessage id="common.maxLength.args" values={{ num: "5" }} />),
			"common.maxLength.50": (<FormattedMessage id="common.maxLength.args" values={{ num: "5" }} />),
			"common.maxLength.80": (<FormattedMessage id="common.maxLength.args" values={{ num: "5" }} />),
			"common.maxLength.100": (<FormattedMessage id="common.maxLength.args" values={{ num: "5" }} />),
			"common.maxLength.160": (<FormattedMessage id="common.maxLength.args" values={{ num: "5" }} />),
			"common.maxLength.200": (<FormattedMessage id="common.maxLength.args" values={{ num: "5" }} />),
			"http.networkError": (
				<FormattedMessage
					id="http.networkError"
					values={formatValues}
				/>
			),
			"http.networkError.args": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `02-0001-${errorNum}` : "02-0001-Unknown",

					}}
				/>
			),
		};
	}, []); // Empty dependency array since formatValues is static and doesn't depend on intl

	const [intervalIds] = React.useState([]);
	const [accountData, setAccountData] = React.useState({...appContext.accountData});
	const [contactData, setContactData] = React.useState({...appContext.contactData});
	const [firstError, setFirstError] = React.useState('');
	const [errorFocus, setErrorFocus] = React.useState(false);

	/**
	 * This function is meant to be called repeatedly through a setInterval()
	 * It either gradually shows or hides the tabs on the screen and then clears the interval when it has completed.
	 * If it's hiding the tabs, it will then automatically call the function to update the tabs.
	 *
	 * animationTimerID - either "show" or "hide", and accesses the object with that key for the intervalId to clear itself from continuous running
	 * startTime - the time when the function is first run, for figuring out what % to set the opacity to based on how long past the start time it is
	 * newArr - only used when we are subsequently updating the tabs array, just passed through to the function that updates the tabs
	 */
	const updateCSS = (animationTimerID, startTime, newArr, tabToClick) => {
		let duration = 200;
		let nextPercent = (Date.now() - startTime) / duration;

		if (nextPercent > 1) {
			nextPercent = 1;
		}
		if (document.getElementById("tabsDiv")) {
			document.getElementById("tabsDiv").style.opacity = (animationTimerID === "hide" ? 1 - nextPercent : nextPercent);
		}
		if (nextPercent === 1) {
			clearInterval(intervalIds[animationTimerID]);
		}
	}

	useEffect(() => {
		// Initialize the WET plugin(s)
		if (window.wb) {
			document.getElementById("tabsDiv").style.opacity = 0;
			window.wb.add(".wb-tabs");

			setTimeout(() => {
				let startTime = Date.now();
				intervalIds["show"] = setInterval(() => {
						updateCSS("show", startTime);
					}
					, 1
				);
			}, 400)
		}

		//load data from GCCase if they are an existing user
		if (!!appContext.existingUser) {
			loadSceneDataFromApi();
		}
	}, []);

	useEffect(() => {
		// Prevent submit button to be triggered when the 'ace_businessnumber' field is focused
		// In which case it triggers the 'search-MDM' button when pressing 'Enter'
		const handleKeyPress = (event) => {
			if (event.key === 'Enter') {
			const focusedElement = document.activeElement;
				if (focusedElement.id === 'ace_businessnumber') {
					event.preventDefault(); // Prevent form submission
					document.getElementById('search-MDM').click();
				}
			}
		};

		// Add event listener
		document.addEventListener('keydown', handleKeyPress);

		// Clean up the event listener
		return () => {
			document.removeEventListener('keydown', handleKeyPress);
		};
	}, []);

	function isItTimeToRefreshData() {
		return !contactData
		       || !contactData.lastDataRetrievalDate
		       || (typeof contactData.lastDataRetrievalDate !== "number")
		       || Date.now() > contactData.lastDataRetrievalDate + 30000
		       || !accountData
		       || !accountData.lastDataRetrievalDate
		       || (typeof accountData.lastDataRetrievalDate !== "number")
		       || Date.now() > accountData.lastDataRetrievalDate + 30000;
	}

	const loadSceneDataFromApi = () => {
		Promise.all([
			loadAccountDataFromApi()
			, loadContactDataFromApi()
		]).catch((error)=>{

			if (window.env.environment !== "prod") {
				console.log({error})
			}

			if (window.env.environment !== "local") {
				let errorNum = error?.response?.data?.Error?.ErrorNumber // ErrorNumber specific to the lambda

				if (!!errorNum) {
					appContext.setFlashError({duration:-1, messages:[labels["http.networkError.args"](errorNum)]});
				} else {
					appContext.setFlashError({duration:-1, messages:[labels["http.networkError"]]});
				}
			}
		}).finally(() => {
			// if (window.env.environment !== "dev") { // prevents the BN to be searched because it doesn't work in main-dev
				if (!!accountData.ace_businessnumber) {
					document.getElementById("search-MDM").click();
				}
			// }
		});
	}

	const loadAccountDataFromApi = async () => {
		if (isItTimeToRefreshData()) {
			let apiURL = "account";

			// If currentLangParam().lang is set, use that in apiURL to call lambda
			if (!!currentLang()) {
				apiURL += `?lang=${currentLang()}`;
			}

			return API.get("backend-api", apiURL, {}).then(function(response){
				let newAccountData = {...appContext.accountData, ...response};
				newAccountData.lastDataRetrievalDate = Date.now();
				appContext.setAccountData(newAccountData);

				setAccountData(newAccountData);
			}).catch((err) => {
				if (window.env.environment === "local") {
					let dummyAccountData = {lastDataRetrievalDate: Date.now(), ...window.env["accountData"]};
					appContext.setAccountData(dummyAccountData);
					setAccountData(dummyAccountData);	//local env, fill with dummy data
					return;
				}
				throw err;
			});
		}
	}

	const loadContactDataFromApi = async () => {
		if (isItTimeToRefreshData()) {
			let apiURL = "contact";

			// If currentLangParam().lang is set, use that in apiURL to call lambda
			if (!!currentLang()) {
				apiURL += `?lang=${currentLang()}`;
			}

			return API.get("backend-api", apiURL, {}).then(function(response){
				let newContactData = {...appContext.contactData, ...response};
				newContactData.lastDataRetrievalDate = Date.now();
				appContext.setContactData(newContactData);

				setContactData(newContactData);

			}).catch((err) => {
				if (window.env.environment === "local") {
					let dummyContactData = {lastDataRetrievalDate: Date.now(), ...window.env["contactData"]};
					appContext.setContactData(dummyContactData);
					setContactData(dummyContactData);	//local env, fill with dummy data
					return;
				}
				throw err;
			});
		}
	}

	let data = [
		{
			tabLabel: <FormattedMessage id="sima.account.tab.accountInfo.account"/>
			, enableTabNumbering: true
			, tabContent:
				<>
				  <AccountInfoTabBody navigation={navigation} />
				</>
			, validationSchema: {
				ace_businessnumber: Yup.string().required(messages["common.isRequired"]).max(15, labels["common.maxLength.15"]).matches(/^[0-9]{9}[R|r][M|m][0-9]{4}/, messages["common.mustBeAValidBN"])
			}
		}
		, {
			tabLabel: <FormattedMessage id="sima.account.tab.accountInfo.contact"/>
			, enableTabNumbering: true
			, tabContent:
				<>
				  <ContactInfoTabBody navigation={navigation} />
				</>
			, validationSchema: {
				firstname: Yup.string().required(messages["common.isRequired"]).max(200, labels["common.maxLength.200"])
				, lastname: Yup.string().required(messages["common.isRequired"]).max(200, labels["common.maxLength.200"])
				, address: Yup.string().required(messages["common.isRequired"]).max(200, labels["common.maxLength.200"]).matches(/^[\p{L}\p{N} \-,'.]*$/u, messages["common.mustBeAValidAddress"])
				, city: Yup.string().required(messages["common.isRequired"]).max(100, labels["common.maxLength.100"]).matches(/^[\p{L}\p{N} \-,'.]*$/u, messages["common.mustBeAValidCity"])
				, country: Yup.string().required(messages["common.isRequired"])
				, provinceState: Yup.string().when('country', {
					is: (country) => country === constants.countries["Canada"] || country === constants.countries["United States"]
					, then: Yup.string().required(messages["common.isRequired"])
				})
				, provinceStateOther: Yup.string().max(100, labels["common.maxLength.100"])
				, postalZipCode: Yup.string().when('country', {
					is: (country) => country === constants.countries["Canada"] || country === constants.countries["United States"]
					, then: Yup.string().required(messages["common.isRequired"])
				}).test('postalCode-validation', messages['common.mustBeAValidPostalCode'], function(value) {
					const { country } = this.parent;

					// Validate zip code using postal-codes.js based on the selected country
					let PCErrorMsg = PostalCodes.validate(constants.countryGUIDToAlpha2[country], value);

					// if it starts with anything else than 'Unknown' or 'Missing'
					if (!(PCErrorMsg !== true && (PCErrorMsg.startsWith('Unknown') || PCErrorMsg.startsWith('Missing')))) {
						// if it isn't true, meaning there is still an error
						if (PCErrorMsg !== true) {
							PCErrorMsg = false;
						}
					// else we won't be checking the error, or -- it's already true
					} else {
						PCErrorMsg = true;
					}

					return PCErrorMsg;
				})
				, languagePreference: Yup.string().required(messages["common.isRequired"])
			}
		}
		, {
			tabLabel: <FormattedMessage id="home.tab.submit" />
			, enableTabNumbering: true
			, tabContent:
				<SubmitTabBody navigation={navigation} checkSpamForEmailsWarning={!appContext.existingUser} setErrorFocus={setErrorFocus} />
			, validationSchema: {
				certificationField: Yup.array().compact().length(1, messages["common.isRequired"])
			}
		}
	];


	function handleSubmit(values) {

		appContext.setGlobalSpinnerCount(appContext.globalSpinnerCount + 1);
		new Promise((resolve, reject) => {
			(async () => {
				try {
					await submitContactData(values);
					await submitAccountData(values);
					resolve(); // Resolve the promise when all actions are completed
				} catch (error) {
					reject(error); // Reject the promise if an error occurs
				}
			})();
		}).then(() => {
			let navigateToKey = localStorage.getItem("activeKey");
			if (!navigateToKey) {
				navigateToKey = "Account";
			}
			navigation.navigate(navigateToKey, currentLangParam());
		}).catch(async (error) => {
			if (window.env.environment !== "prod") {
				console.log({error});
			}
			if (!!error.refreshSession) {
				try {
					await Auth.currentAuthenticatedUser({bypassCache: true}); //refreshing the session tokens because there was a change to user attributes stored in Cognito
				} catch (error) {
					console.log("error refreshing user's session token after user attribute update, recommend logging out and logging back in to grab the modified attributes: ", error);
				}
			}

			appContext.setFlashError({duration:-1, messages:[labels["http.networkError"]]});
		}).finally(() => {
			appContext.setGlobalSpinnerCount(Math.max(appContext.globalSpinnerCount - 1, 0));
		});

	}

	async function submitContactData(values) {

		//have the values changed
		if (appContext.contactData.firstname !== values.firstname
			|| appContext.contactData.lastname !== values.lastname
			|| appContext.contactData.address !== values.address
			|| appContext.contactData.city !== values.city
			|| appContext.contactData.country !== values.country
			|| appContext.contactData.provinceState !== values.provinceState
			|| appContext.contactData.provinceStateOther !== values.provinceStateOther
			|| appContext.contactData.postalZipCode !== values.postalZipCode
			|| appContext.contactData.languagePreference !== values.languagePreference
		) {

			let updatedContactData = {
				firstname: values.firstname
				, lastname: values.lastname
				, ace_address: values.address
				, ace_city: values.city
				, ace_countryregion: values.country
				, ace_provincestate: values.provinceState
				, ace_provincestatetext: values.provinceStateOther
				, ace_zippostalcode: values.postalZipCode
				, languagePreference: values.languagePreference
			};

			if (values.country === constants.countries["Canada"] || values.country === constants.countries["United States"] || values.country === constants.countries["Mexico"]) {
				delete updatedContactData.ace_provincestatetext;
			} else {
				delete updatedContactData.ace_provincestate;
			}

			// only send non-empty strings
			let filteredContactData = Object.fromEntries(
				Object.entries(updatedContactData).filter(([key, value]) => value !== "")
			);

			let payload = {
				body: filteredContactData
			};


			let apiURL = `updateContact`;

			return API.post("backend-api", apiURL, payload).then(async function(response){
				updatedContactData.lastDataRetrievalDate = Date.now();

				appContext.setContactData({...appContext.contactData, ...updatedContactData});	//merge with existing data to preserve keys like "ace_urn"
				appContext.setExistingUser(true);
				if (response.refreshSession) {
					try {
						await Auth.currentAuthenticatedUser({bypassCache: true});	//refreshing the session tokens because there was a change to user attributes stored in Cognito
					} catch (error) {
						console.log("error refreshing user's ID token after user attribute update, recommend logging out and logging back in to grab the modified attributes: ", error);
					}
				}
			});
		}
	}

	async function submitAccountData(values) {
		let ace_accounttypeGUID = ""

		//has the data been updated?
		if (((appContext.accountData?.ace_businessnumber?.toUpperCase() ?? "") !== values?.ace_businessnumber.toUpperCase())
			|| (appContext.accountData?.registrantInfoFirm !== values?.registrantInfoFirm)) {
			if (!!values.ace_businessnumber) {
				values.ace_businessnumber = values.ace_businessnumber.toUpperCase();
			}
			if (values.registrantInfoFirm?.[0] === "yes") {
				ace_accounttypeGUID = constants.registrantInfoFirm
			}
			let payload = {
				body: {
					ace_businessnumber: values.ace_businessnumber
					, ace_accounttype: ace_accounttypeGUID
				}
			};

			let apiURL = `updateAccount`;
			return API.post("backend-api", apiURL, payload).then(async function(response){
				//saving the MDM data in memory for later
				//TODO - ensure that this MDM data is actually what is used on the account display page and align if necessary
				let updatedAccountData = {
					ace_businessnumber: response.ace_businessnumber
					, companyLegalName: response.Name
					, companyOperatingName: response.Name
					, companyAddress: response.ace_street
					, companyCity: response.ace_city
					, companyCountry: response.dtoCountryRegion
					, companyProvince: response.dtoProvinceState	//TODO - this is coming in as free text, but it's going to be treated as a GUID, address this.
					, companyProvinceStateOther: response.ace_provincestate //TODO - probably should only set either this province/state field or the previous province/state field
					, companyPostalZipCode: response.ace_postalzipcode
					, Telephone1: response.Telephone1
					, ace_accounttype: response.ace_accounttype
				};
				updatedAccountData.lastDataRetrievalDate = Date.now();
				appContext.setAccountData(updatedAccountData);
			});
		}
	}

	const {scrollTo} = useScrollTo(config.molasses);	//<-- usage: https://github.com/TomasSestak/react-spring-scroll-to-hook

	const guidPattern = /^[a-zA-Z0-9-]{36}$/

	return (
		<>
		  <div>
		    <Formik
		      enableReinitialize
		      initialValues={{
		      	  ace_businessnumber:(!!accountData["ace_businessnumber"] ? accountData["ace_businessnumber"] : "")
		      	, companyLegalName:(!!accountData["companyLegalName"] ? accountData["companyLegalName"] : "")
		      	, companyOperatingName:(!!accountData["companyOperatingName"] ? accountData["companyOperatingName"] : "")
		      	, contactFirstName:(!!accountData["contactFirstName"] ? accountData["contactFirstName"] : "")
		      	, contactLastName:(!!accountData["contactLastName"] ? accountData["contactLastName"] : "")
		      	, companyAddress:accountData["companyAddress"]
		      	, companyCity:accountData["companyCity"]
		      	, companyCountry:accountData["companyCountry"]
		      	, companyProvince:accountData["companyProvince"]
		      	, companyProvinceStateOther:accountData["companyProvinceState"]
		      	, companyPostalZipCode:accountData["companyPostalZipCode"] ?? ""
		      	, Telephone1:accountData["Telephone1"]
		      	, registrantInfoFirm: (accountData.ace_accounttype === constants.registrantInfoFirm ? ["yes"] : null)

		      	, ace_urn:(!!contactData["ace_urn"] ? contactData["ace_urn"] : "")
		      	, firstname:(!!contactData["firstname"] ? contactData["firstname"] : "")
		      	, lastname:(!!contactData["lastname"] ? contactData["lastname"] : "")
		      	, emailaddress1:contactData["emailaddress1"]
		      	, address:(!!contactData["ace_address"] ? contactData["ace_address"] : "")
		      	, city:contactData["ace_city"]
		      	, country:contactData["ace_countryregion"]
		      	, provinceState: guidPattern.test(contactData["ace_provincestate"]) ? contactData["ace_provincestate"] : ""
		      	, provinceStateOther: guidPattern.test(contactData["ace_provincestate"]) ? "" : contactData["ace_provincestatetext"]
		      	, postalZipCode: contactData["ace_zippostalcode"]
		      	, languagePreference:contactData["languagePreference"]

		      	, certificationField:[]
		      }}
		      validationSchema={Yup.object({
		      	...data[0].validationSchema
		      	, ...data[1].validationSchema
		      	, ...data[2].validationSchema
		      })}
		      onSubmit={(values, formikBag) => {
		      	handleSubmit(values);
		      	formikBag.setSubmitting(false);
		      }}
		    >
		      {({ errors, isSubmitting, submitCount, setSubmitting }) => {
		      	if (submitCount > 0) {
		      		if (isSubmitting && JSON.stringify(errors, null, 2) !== "{}") {
		      			let errorMsgs = [messages["comment.flash.validationErrors"]];
		      			setFirstError(Object.keys(errors)[0]);
		      			for (const [key, value] of Object.entries(errors)) {
		      				let errMsg = "";
		      				let labelElement = document.querySelector("label[for='"+key+"']");
		      				if (!!labelElement) {
		      					errMsg += labelElement.firstChild.nodeValue.trim();
		      				}
		      				if (errMsg === "") {
		      					errMsg += messages[key];
		      				}
		      				errMsg += messages["label.colon"] + " " +value;
		      				let elementId;
		      				if (key === "certificationField") {
		      					elementId = document.getElementById(key+'-0');
		      				} else {
		      					elementId = document.getElementById(key);
		      				}
		      				errorMsgs.push(<ErrorValidationSpan errMsg={errMsg} elementId={elementId} keyEl={key} />);
		      			}
		      			appContext.setFlashError({duration:-1, messages: errorMsgs});
		      			scrollTo(document.querySelector("#mouse-enter-e"));
		      			setSubmitting(false); //prevent infinite refresh loops
		      		} else if (JSON.stringify(errors, null, 2) === "{}") {
		      			appContext.setFlashError(null)
		      		}
		      		if (errorFocus === true) {
		      			try {
		      				document.getElementById("span-"+firstError).focus();
		      				setErrorFocus(false);
		      			} catch {}
		      		}
		      	}
		      	return (
		      		<Form>
		      		  <WETTabs tabs={data} submitCount={submitCount} errors={errors}/>
		      		</Form>
		      	)
		      }}
		    </Formik>
		  </div>
		</>
	);
}

Account_Edit.path = "";
Account_Edit.navigationOptions = {
	pageTitle: <FormattedMessage id="sima.account.edit.title"/>
	, breadcrumb: [
		<PopupLink url={window.env["canada-url"][currentLang()]}><FormattedMessage id="breadcrumb.Canada.text" /></PopupLink>
		, <PopupLink url={window.env["cbsa-breadcrumbs-url"][currentLang()]}><FormattedMessage id="breadcrumb.CBSA.text" /></PopupLink>
		, <PopupLink url={window.env["cbsa-sima-breadcrumbs-url"][currentLang()]}><FormattedMessage id="breadcrumb.ACE.text" /></PopupLink>
		, <Link routeName="Account" params={currentLangParam()}><FormattedMessage id="account.title"/></Link>
	]
	, breadcrumbTitle: <FormattedMessage id="account.edit.breadcrumbs"/>
};

export default Account_Edit;